import React, { useState } from 'react';
import { ICONS, MARKETING_ORIGIN } from '../constants';
import { useAuth } from '../contexts';
import { reportError } from '../services/errorReporting';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose }) => {
  const { signInWithGoogle, sendSignInLink, emailLinkError, clearEmailLinkError } = useAuth();
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    clearEmailLinkError();
    onClose();
  };

  if (!isOpen) return null;

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    clearEmailLinkError();
    setLoading(true);

    try {
      await sendSignInLink(email);
      setSuccessMessage('שלחנו לך לינק להתחברות למייל. בדוק את תיבת הדואר שלך והתחבר דרך הקישור.');
    } catch (err: any) {
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setSuccessMessage('');
    setLoading(true);
    try {
      await signInWithGoogle();
      handleClose();
    } catch (err: any) {
      setError(getErrorMessage(err.code));
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (code: string): string => {
    switch (code) {
      case 'auth/invalid-email':
        return 'כתובת אימייל לא תקינה';
      case 'auth/user-disabled':
        return 'המשתמש חסום';
      case 'auth/user-not-found':
        return 'משתמש לא נמצא';
      case 'auth/wrong-password':
        return 'סיסמה שגויה';
      case 'auth/email-already-in-use':
        return 'האימייל כבר משויך לחשבון. התחבר עם Google או בקש לינק חדש למייל זה.';
      case 'auth/weak-password':
        return 'הסיסמה חלשה מדי (מינימום 6 תווים)';
      case 'auth/popup-closed-by-user':
        return 'החלון נסגר לפני השלמת ההתחברות';
      case 'auth/account-exists-with-different-credential':
        return 'נראה שכבר יש חשבון עבור האימייל הזה עם שיטת התחברות אחרת. נסה \"המשך עם Google\" או בקש לינק התחברות למייל.';
      case 'auth/too-many-requests':
        return 'בוצעו יותר מדי נסיונות התחברות. נסה שוב מאוחר יותר.';
      case 'auth/api-key-not-valid':
      case 'auth/invalid-api-key':
        reportError(new Error(`Firebase API key config error: ${code}`), { module: 'auth', extra: { action: 'login', code } });
        return 'שגיאת הגדרות מערכת. צור קשר עם התמיכה.';
      default:
        if (code?.includes?.('api-key-not-valid') || code?.includes?.('invalid-api-key')) {
          reportError(new Error(`Firebase API key config error: ${code}`), { module: 'auth', extra: { action: 'login', code } });
          return 'שגיאת הגדרות מערכת. צור קשר עם התמיכה.';
        }
        return 'שגיאה בהתחברות. נסה שוב.';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-click-card border border-click-border rounded-2xl max-w-md w-full p-6 sm:p-8 relative m-4 max-h-[90vh] overflow-y-auto">
        <button 
          onClick={handleClose}
          className="absolute top-4 left-4 text-gray-500 hover:text-white transition-colors"
        >
          <ICONS.X className="w-6 h-6" />
        </button>

        <div className="text-center mb-8">
          <ICONS.FlaskConical className="w-12 h-12 text-click-red mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-2">
            כניסה למעבדה
          </h2>
          <p className="text-gray-400 text-sm">
            היכנס בעזרת Google או קבל לינק התחברות למייל שלך (ללא סיסמה).
          </p>
        </div>

        {emailLinkError && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg mb-6 text-sm text-center flex items-center justify-between gap-2">
            <span className="flex-1">{emailLinkError}</span>
            <button type="button" onClick={clearEmailLinkError} className="shrink-0 text-red-300 hover:text-white" aria-label="סגור">
              <ICONS.X className="w-4 h-4" />
            </button>
          </div>
        )}

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg mb-6 text-sm text-center">
            {error}
          </div>
        )}

      {successMessage && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 p-3 rounded-lg mb-6 text-sm text-center">
          {successMessage}
        </div>
      )}

        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          className="w-full bg-white text-gray-800 font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-3 hover:bg-gray-100 transition-colors mb-6 disabled:opacity-50"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24">
            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          {loading ? 'מתחבר...' : 'המשך עם Google'}
        </button>

        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-click-border"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-click-card text-gray-500">או</span>
          </div>
        </div>

        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <div>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="אימייל"
              required
              className="w-full bg-click-dark border border-click-border rounded-xl px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-click-red transition-colors"
              dir="ltr"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-click-red text-white font-bold py-3 rounded-xl hover:bg-white hover:text-click-red transition-all disabled:opacity-50"
          >
            {loading ? 'שולח לינק...' : 'שלח לינק התחברות למייל'}
          </button>
        </form>

        <p className="text-center text-gray-500 text-xs mt-6">
          בהרשמה אתה מסכים ל־
          <a href={`${MARKETING_ORIGIN}/terms`} target="_blank" rel="noopener noreferrer" className="text-click-red hover:underline">תנאי שימוש</a>
          , <a href={`${MARKETING_ORIGIN}/privacy`} target="_blank" rel="noopener noreferrer" className="text-click-red hover:underline">מדיניות פרטיות</a>
          {' ו־'}
          <a href={`${MARKETING_ORIGIN}/cancellation`} target="_blank" rel="noopener noreferrer" className="text-click-red hover:underline">מדיניות ביטולים</a>.
        </p>
      </div>
    </div>
  );
};

export default LoginModal;
